import { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle, Home, LogOut, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

interface ChunkErrorFallbackProps {
  error?: Error;
  resetErrorBoundary?: () => void;
}

export const ChunkErrorFallback = ({ error, resetErrorBoundary }: ChunkErrorFallbackProps) => {
  const [countdown, setCountdown] = useState(5);
  const [hasAttemptedReload, setHasAttemptedReload] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const isChunkError = error?.message?.includes('Failed to fetch dynamically imported module') ||
                       error?.message?.includes('Loading chunk') ||
                       error?.message?.includes('Loading CSS chunk') ||
                       error?.message?.includes('Importing a module script failed');

  useEffect(() => {
    // Check if we've already attempted a reload for this session
    const reloadAttempted = sessionStorage.getItem('chunk_fallback_reload') === 'true';
    setHasAttemptedReload(reloadAttempted);

    if (isChunkError && !reloadAttempted) {
      // Auto-refresh countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleRefresh();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isChunkError]);

  const handleRefresh = () => {
    sessionStorage.setItem('chunk_fallback_reload', 'true');
    window.location.reload();
  };

  const handleHardRefresh = async () => {
    setIsClearing(true);
    
    // Clear all session flags
    sessionStorage.removeItem('chunk_fallback_reload');
    sessionStorage.removeItem('chunk_error_reload');
    sessionStorage.removeItem('page_reloaded_for_chunk');
    
    // Clear service worker caches if available
    if ('caches' in window) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map((name) => caches.delete(name)));
      } catch (e) {
        console.error('Cache clear failed:', e);
      }
    }
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      } catch (e) {
        console.error('Service worker unregister failed:', e);
      }
    }
    
    // Force hard reload bypassing cache
    window.location.href = window.location.origin + '/home?cache_bust=' + Date.now();
  };

  const handleGoHome = () => {
    // Clear flags and go to home
    sessionStorage.removeItem('chunk_fallback_reload');
    sessionStorage.removeItem('chunk_error_reload');
    sessionStorage.removeItem('page_reloaded_for_chunk');
    window.location.href = window.location.origin + '/home';
  };

  // Check if stuck on onboarding page
  const isOnOnboarding = window.location.pathname === '/onboarding';

  const handleSkipOnboarding = async () => {
    try {
      setIsClearing(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ has_completed_onboarding: true })
          .eq('id', user.id);
      }
      // Clear flags
      sessionStorage.removeItem('chunk_fallback_reload');
      sessionStorage.removeItem('chunk_error_reload');
      sessionStorage.removeItem('page_reloaded_for_chunk');
      window.location.href = window.location.origin + '/first-skin-sync';
    } catch (e) {
      console.error('Skip onboarding failed:', e);
      window.location.href = window.location.origin + '/home';
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      sessionStorage.clear();
      window.location.href = window.location.origin + '/login';
    } catch (e) {
      console.error('Logout failed:', e);
      window.location.href = window.location.origin + '/login';
    }
  };

  if (isChunkError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <RefreshCw className={`w-8 h-8 text-primary ${isClearing ? 'animate-spin' : ''}`} />
            </div>
            
            <h2 className="text-xl font-semibold">Updating App...</h2>
            
            {!hasAttemptedReload ? (
              <>
                <p className="text-muted-foreground">
                  We've released a new version. Refreshing automatically in {countdown} seconds...
                </p>
                <Button onClick={handleRefresh} className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Now
                </Button>
              </>
            ) : (
              <>
                <p className="text-muted-foreground">
                  {isOnOnboarding 
                    ? "We're having trouble loading the onboarding page. You can skip it or try again later."
                    : "The automatic refresh didn't work. Try going to the home page or clearing your cache."
                  }
                </p>
                <div className="space-y-2">
                  {isOnOnboarding ? (
                    <>
                      <Button onClick={handleSkipOnboarding} className="w-full" disabled={isClearing}>
                        <SkipForward className="w-4 h-4 mr-2" />
                        {isClearing ? 'Skipping...' : 'Skip Onboarding & Continue'}
                      </Button>
                      <Button onClick={handleHardRefresh} variant="outline" className="w-full" disabled={isClearing}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${isClearing ? 'animate-spin' : ''}`} />
                        Clear Cache & Retry
                      </Button>
                      <Button onClick={handleLogout} variant="ghost" className="w-full">
                        <LogOut className="w-4 h-4 mr-2" />
                        Log Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={handleGoHome} className="w-full">
                        <Home className="w-4 h-4 mr-2" />
                        Go to Home
                      </Button>
                      <Button 
                        onClick={handleHardRefresh} 
                        variant="outline" 
                        className="w-full"
                        disabled={isClearing}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${isClearing ? 'animate-spin' : ''}`} />
                        {isClearing ? 'Clearing Cache...' : 'Clear Cache & Reload'}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        Or press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Cmd+Shift+R</kbd> (Mac) / <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+Shift+R</kbd> (Windows)
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Generic error fallback for non-chunk errors
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          
          <h2 className="text-xl font-semibold">Something went wrong</h2>
          <p className="text-muted-foreground">
            We encountered an unexpected error. Please try refreshing the page.
          </p>
          
          <div className="space-y-2">
            <Button onClick={handleGoHome} className="w-full">
              <Home className="w-4 h-4 mr-2" />
              Go to Home
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Page
            </Button>
            {resetErrorBoundary && (
              <Button variant="ghost" onClick={resetErrorBoundary} className="w-full">
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChunkErrorFallback;
